import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Modal from 'live/components/ui/Modal'
import DatabaseLoadingBar from './DatabaseLoadingBar'
import '../main/options/modalFull.scss'
import { CheckedFileDVO } from './interface/Group'
import { useStreamingState } from './hooks/UseStreamingState'
import { BASE_SERVICE_URL } from '../../lib/constants'
import { MsgErrorRows, TypesError } from '../alerts/StatusResponse'


interface Props {
    showModal: boolean
    setShowModal: (show: boolean) => void
    filesToImport: FileToImportStatus[]
    finishProcessData: () => void
}

interface FileToImportStatus {
    checkedFileDVO: CheckedFileDVO
    percent: number
    error?: boolean
    status: string
    message: string
}

export default function ProcessDataModal({ showModal, setShowModal, filesToImport, finishProcessData }: Props) {
    const [dataToBeProcessed, setDataToBeProcessed] = useState([...filesToImport])
    const [disableFinishProcessDataButton, setDisableFinishProcessDataButton] = useState<boolean>(false)
    const [processingDataIndex, setProcessingDataIndex] = useState<number>(0);

    useEffect(() => {
        if (dataToBeProcessed) {
            const fileData = dataToBeProcessed[processingDataIndex]

            const formData = new FormData()
            formData.append('uid', fileData.checkedFileDVO.uid)
            formData.append('fileName', fileData.checkedFileDVO.fileName)
            formData.append('sheetIndex', fileData.checkedFileDVO.sheetIndex)
            formData.append('eventType', fileData.checkedFileDVO.eventType)
            formData.append('username', window.Live.authorization.user.username)
            formData.append('dateReference', fileData.checkedFileDVO.dateReference)

            try {
                let data = {
                    "Type": "Data is being uploaded",
                    "messages": [
                        {
                            "message": "0",
                            "status": "0 events are loaded\n"
                        }
                    ]
                }

                useStreamingState(
                    {
                        reqInfo: BASE_SERVICE_URL + `/models/upload/${fileData.checkedFileDVO.database}`,
                        reqInit: {
                            method: 'POST',
                            body: formData,
                            credentials: 'same-origin'
                        },
                        streamBuffer: true,
                        dataSeparator: "__"
                    },
                    state => {

                        if (state.done) {
                            const dataToBeProcessedDTO = [...dataToBeProcessed]
                            dataToBeProcessedDTO[processingDataIndex].percent = 100
                            setDataToBeProcessed([...dataToBeProcessedDTO])

                            if (processingDataIndex + 1 < dataToBeProcessed.length) {
                                setProcessingDataIndex(processingDataIndex + 1)
                            }
                        } else {
                            const dataToBeProcessedDTO = [...dataToBeProcessed]
                            try {
                                let dataString = state.data[state.data.length - 1];
                                console.log(dataString)
                                if (dataString.startsWith('Error - ')) {
                                    dataString = dataString.slice(8)

                                }
                                data = JSON.parse(dataString) || {}
                            }
                            catch (error) {
                                console.log(error)
                            }

                            if (Object.keys(data).includes('success') && !data['success'] && !!dataToBeProcessedDTO[processingDataIndex].error) {
                                dataToBeProcessedDTO[processingDataIndex].error = true
                            }
                            else {
                                const status = data?.status || String(data?.messages[0]?.status);
                                const type = String(data?.Type || data?.type);

                                switch (status) {
                                    case 'ERROR': {
                                        dataToBeProcessedDTO[processingDataIndex].error = true
                                        dataToBeProcessedDTO[processingDataIndex].status = type
                                        dataToBeProcessedDTO[processingDataIndex].percent = 0
                                        dataToBeProcessedDTO[processingDataIndex].message = getMessageError(data, type)
                                        break;
                                    }
                                    default: {
                                        dataToBeProcessedDTO[processingDataIndex].status = String(data?.Type)
                                        const percent = Number(data.messages[0].message)
                                        dataToBeProcessedDTO[processingDataIndex].percent = percent > 100 ? 100 : percent
                                        dataToBeProcessedDTO[processingDataIndex].message = status
                                    }
                                }
                                setDataToBeProcessed([...dataToBeProcessedDTO])
                            }
                        }
                    }
                )
            }
            catch (error) {
                dataToBeProcessed[processingDataIndex].error = true
                if (processingDataIndex + 1 < dataToBeProcessed.length) {
                    setProcessingDataIndex(processingDataIndex + 1)
                }
            }
        }
    }, [processingDataIndex])


    useEffect(() => {
        setDisableFinishProcessDataButton(isProcessingData())
    }, [dataToBeProcessed])

    const isProcessingData = (): boolean => {
        return dataToBeProcessed.filter(data => {
            return data.percent < 100 && !data.error
        }).length > 0
    }

    return (
        <Modal show={showModal} backdrop="static" keyboard={false} className="groupDatabaseModal">
            <Modal.Header>
                <Modal.Title>
                    <b>PROCESSING DATA TO DATABASES</b>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FileDataTable>
                    <table>
                        <thead>
                            <tr>
                                <th>FILE NAME</th>
                                <th>UPLOAD STATUS</th>
                                <th style={{ minWidth: '300px' }}>MESSAGE</th>
                                <th style={{ minWidth: '200px' }}>STATUS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dataToBeProcessed.map(data => (
                                <tr>
                                    <td>{data.checkedFileDVO?.fileName}</td>
                                    <td>
                                        <DatabaseLoadingBar percent={data?.percent} error={data.error} />
                                    </td>
                                    <td>
                                        {data?.message}
                                    </td>
                                    <td>
                                        {
                                            data.error == true ? 'Error' :
                                                String(data?.percent) === '100' ? 'OK' : data?.status ?? 'Pending'
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </FileDataTable>
            </Modal.Body>
            <Modal.Footer>
                <FooterContainer>
                    <button
                        onClick={() => {
                            finishProcessData()
                            setShowModal(false)
                        }}
                        className={`button--processData${disableFinishProcessDataButton ? '__disabled' : ''}`}
                        disabled={disableFinishProcessDataButton}
                    >
                        {' '}
                        Ok{' '}
                    </button>
                </FooterContainer>
            </Modal.Footer>
        </Modal>
    )
}

function getMessageError(data, type: string): string {
    if (type == TypesError.Rows) {
        const erroObj = data as MsgErrorRows
        const firstError = erroObj.details.find(e => e.status != "OK")
        return `Column: ${firstError.columnName}
        message: ${firstError.messages[0] || ''}`
    } else if (type == TypesError.Header) {
        return `Invalid Header`
    } else if (!!data?.message) {
        return String(data?.message)
    }
    return `ERROR`
}

const FooterContainer = styled.div`
    .button {
        &--processData {
            width: 131px;
            height: 32px;
            color: var(--gray-0);
            background: var(--blue-500);
            border: none;
            border-radius: 3px;
            font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
            margin-right: 16px;
            &__disabled{
                width: 131px;
                height: 32px;
                color: var(--blue-200);
                background: var(--blue-400);
                border: none;
                border-radius: 3px;
                font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
                margin-right: 16px;
            }
        }
    }
`

const FileDataTable = styled.div`
    width: 100%;    
    max-height: 300px;
    overflow-y: auto;    
    table{
        width: 100%;    
    }
    th {
        color: var(--gray-800);
        padding: 4px 8px;
        background: var(--gray-0);
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        size: 11px;
    }
    td {
        color: var(--gray-700);
        padding: 4px 8px;
        background: var(--gray-0);
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        font-size: 13px;
    }
    tr {
        border-bottom: 1px solid var(--gray-400);
        height: 32px;
    }
`
